/**
 * OpenAI GPT Image 系 model_code のファミリー判定・表示名ユーティリティ。
 *
 * model_code は `{api_model}-{quality}` 形式 (例: gpt-image-2.5-flare-high)。
 * 2026-09-08 の GPT Image 2.5 (Flare / Sunburst) 導入で OpenAI 枠が複数ファミリーになった
 * ため、「gpt-image- で始まる = GPT Image 2」という前提を置いていた表示箇所を
 * ここに集約する。表示名は generation_models.model_family_name を正本にし、
 * 行が引けない (旧モデル・非表示モデル・履歴表示) 場合のみ model_code から補う。
 */

/** OpenAI Images API の quality 値。xhigh / max は 2.5 系で追加。
 *  backend/services/openai_image_provider._QUALITY_SUFFIXES と同期。 */
export const GPT_IMAGE_QUALITY_SUFFIXES = ['auto', 'low', 'medium', 'high', 'xhigh', 'max'] as const;
export type GptImageQuality = (typeof GPT_IMAGE_QUALITY_SUFFIXES)[number];

const QUALITY_SUFFIX_RE = new RegExp(`-(${GPT_IMAGE_QUALITY_SUFFIXES.join('|')})$`);

/** OpenAI GPT Image 系の model_code か (backend openai_image_provider.is_openai_image_model と同期) */
export function isGptImageModelCode(modelCode: string | null | undefined): boolean {
  return !!modelCode && modelCode.startsWith('gpt-image-');
}

/** model_code から quality 接尾辞を剥がした API モデル名 (= ファミリーキー) を返す。
 *  例: gpt-image-2.5-flare-high → gpt-image-2.5-flare / gpt-image-2-auto → gpt-image-2 */
export function gptImageFamilyKey(modelCode: string): string {
  return modelCode.replace(QUALITY_SUFFIX_RE, '');
}

/** model_code 末尾の quality (無ければ null)。 */
export function gptImageQuality(modelCode: string): GptImageQuality | null {
  const m = modelCode.match(QUALITY_SUFFIX_RE);
  return m ? (m[1] as GptImageQuality) : null;
}

/** ファミリーキーから人間向けファミリー名を組み立てる (model_family_name が引けないときの補完)。
 *  例: gpt-image-2.5-flare → "GPT Image 2.5 Flare" / gpt-image-2 → "GPT Image 2" */
export function gptImageFamilyLabelFromCode(modelCode: string): string {
  const key = gptImageFamilyKey(modelCode);
  const rest = key.startsWith('gpt-image-') ? key.slice('gpt-image-'.length) : key;
  const words = rest
    .split('-')
    .filter(Boolean)
    // 数字始まり (2 / 2.5) はそのまま、それ以外 (flare / sunburst) は Capitalize
    .map((w) => (/^\d/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)));
  return ['GPT Image', ...words].join(' ').trim();
}

/** 正式名称 (表示用ファミリー名)。generation_models 行 (model_family_name) があればそれを正本にする。 */
export function gptImageFamilyLabel(
  modelCode: string,
  models?: ReadonlyArray<{ model_code: string; model_family_name?: string | null }>,
): string {
  const row = models?.find((m) => m.model_code === modelCode);
  const fromDb = row?.model_family_name?.trim();
  if (fromDb) return fromDb;
  return gptImageFamilyLabelFromCode(modelCode);
}

/**
 * ファミリーの通称 (UI 上の呼び名)。
 * 2026-09-08 の GPT Image 2.5 導入時にユーザー指定で「Flare = 標準 / Sunburst = 高品質」とし、
 * 2026-09-09 に Sunburst を「長時間」へ改称した (同品質でも生成時間が Flare の約 2 倍のため、
 * 品質差ではなく所要時間の差として案内する)。
 * 画面には通称を出し、カスタムツールチップで正式名称 (model_family_name) を併記する。
 * 通称の無いファミリー (廃止済みの GPT Image 2 の履歴表示等) は正式名称をそのまま表示する。
 * キーは quality 接尾辞を剥がしたファミリーキー (gptImageFamilyKey)。
 */
export const GPT_IMAGE_FAMILY_ALIASES: Readonly<Record<string, string>> = {
  'gpt-image-2.5-flare': '標準',
  'gpt-image-2.5-sunburst': '長時間',
};

/** ファミリーの通称。無ければ null。 */
export function gptImageFamilyAlias(modelCode: string): string | null {
  return GPT_IMAGE_FAMILY_ALIASES[gptImageFamilyKey(modelCode)] ?? null;
}

/** 画面に出す呼び名。通称があれば通称、無ければ正式名称。 */
export function gptImageFamilyDisplayName(
  modelCode: string,
  models?: ReadonlyArray<{ model_code: string; model_family_name?: string | null }>,
): string {
  return gptImageFamilyAlias(modelCode) ?? gptImageFamilyLabel(modelCode, models);
}

/** 通称を出しているときにツールチップへ添える正式名称。通称が無い (= 正式名称を表示中) なら null。 */
export function gptImageFamilyTooltip(
  modelCode: string,
  models?: ReadonlyArray<{ model_code: string; model_family_name?: string | null }>,
): string | null {
  return gptImageFamilyAlias(modelCode) ? gptImageFamilyLabel(modelCode, models) : null;
}

/** テキストのみの文脈 (トースト等) 用: 通称 + 括弧書きの正式名称。通称が無ければ正式名称のみ。 */
export function gptImageFamilyLongLabel(
  modelCode: string,
  models?: ReadonlyArray<{ model_code: string; model_family_name?: string | null }>,
): string {
  const alias = gptImageFamilyAlias(modelCode);
  const official = gptImageFamilyLabel(modelCode, models);
  return alias ? `${alias} (${official})` : official;
}

/** 生成設定サマリ等で使う短縮表記。
 *  通称があれば "GPT" + 通称 (例: gpt-image-2.5-flare-* → "GPT標準")、
 *  無ければ "GPTi" + バージョン (例: gpt-image-2-* → "GPTi2")。 */
export function gptImageFamilyShortLabel(modelCode: string): string {
  const alias = gptImageFamilyAlias(modelCode);
  if (alias) return `GPT${alias}`;
  const rest = gptImageFamilyKey(modelCode).slice('gpt-image-'.length);
  const [version, ...variant] = rest.split('-').filter(Boolean);
  const variantLabel = variant.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  return variantLabel ? `GPTi${version} ${variantLabel}` : `GPTi${version}`;
}

/** display_label ('GPT高品質' 等) から旧来の "GPT" プレフィックスを落として品質名だけにする。 */
export function stripGptPrefix(displayLabel: string): string {
  return displayLabel.replace(/^GPT/, '');
}

/** バージョン単位のセクションキー。gpt-image-2.5-flare-* / gpt-image-2.5-sunburst-* → 'gpt-image-2.5'、
 *  gpt-image-2-* → 'gpt-image-2' (廃止済み)。UI ではファミリー (Flare / Sunburst) の 1 つ上の階層になる。 */
export function gptImageSectionKey(modelCode: string): string {
  const rest = gptImageFamilyKey(modelCode).slice('gpt-image-'.length);
  const version = rest.split('-').filter(Boolean)[0] ?? rest;
  return `gpt-image-${version}`;
}

/** セクション見出し。'gpt-image-2.5' → "GPT Image 2.5" / 'gpt-image-2' → "GPT Image 2"
 *  (2026-09-09: 旧 UI と同じ「GPT Image 2 / Google Gemini」の並びに揃えるため "GPT Image" 付きに戻した) */
export function gptImageSectionLabel(modelCode: string): string {
  return `GPT Image ${gptImageSectionKey(modelCode).slice('gpt-image-'.length)}`;
}

export interface GptImageFamilyGroup<T> {
  /** ファミリーキー (gpt-image-2.5-flare 等) */
  key: string;
  /** 画面に出す呼び名 (通称があれば通称: 標準 / 高品質、無ければ正式名称: GPT Image 2) */
  label: string;
  /** 正式名称 (GPT Image 2.5 Flare 等) */
  officialLabel: string;
  /** 通称を表示しているときにツールチップへ出す正式名称。正式名称を表示中なら null */
  tooltip: string | null;
  /** グループ内のモデル (sort_order 昇順) */
  list: T[];
}

/**
 * OpenAI 系モデル行をファミリー単位にグルーピングする。
 * グループ順は各グループ先頭の sort_order 昇順 (= DB の並びを尊重)。
 */
export function groupGptImageModelsByFamily<
  T extends { model_code: string; sort_order: number; model_family_name?: string | null },
>(models: ReadonlyArray<T>): GptImageFamilyGroup<T>[] {
  const byKey = new Map<string, GptImageFamilyGroup<T>>();
  [...models]
    .filter((m) => isGptImageModelCode(m.model_code))
    .sort((a, b) => a.sort_order - b.sort_order)
    .forEach((m) => {
      const key = gptImageFamilyKey(m.model_code);
      const group = byKey.get(key);
      if (group) {
        group.list.push(m);
        return;
      }
      byKey.set(key, {
        key,
        label: gptImageFamilyDisplayName(m.model_code, models),
        officialLabel: gptImageFamilyLabel(m.model_code, models),
        tooltip: gptImageFamilyTooltip(m.model_code, models),
        list: [m],
      });
    });
  return [...byKey.values()];
}

export interface GptImageSection<T> {
  /** セクションキー (gpt-image-2.5 等) */
  key: string;
  /** 見出し (GPT Image 2.5) */
  label: string;
  /** セクション内のファミリー (各ファミリー先頭の sort_order 昇順) */
  families: GptImageFamilyGroup<T>[];
}

/**
 * OpenAI 系モデル行を「バージョン (セクション) → ファミリー → 品質」の 3 階層に組み立てる。
 * 例: GPT Image 2.5 → [標準 (Flare: 自動/…/低品質), 長時間 (Sunburst: 自動/…/高品質)]
 *     (GPT 2 は 2026-09-09 に廃止。DB 行が inactive なので通常は 1 セクションのみ)
 */
export function groupGptImageModelsBySection<
  T extends { model_code: string; sort_order: number; model_family_name?: string | null },
>(models: ReadonlyArray<T>): GptImageSection<T>[] {
  const bySection = new Map<string, GptImageSection<T>>();
  groupGptImageModelsByFamily(models).forEach((family) => {
    const sample = family.list[0].model_code;
    const key = gptImageSectionKey(sample);
    const section = bySection.get(key);
    if (section) {
      section.families.push(family);
      return;
    }
    bySection.set(key, { key, label: gptImageSectionLabel(sample), families: [family] });
  });
  return [...bySection.values()];
}
